import { ListBox as AriaListBox, type Key } from "react-aria-components";

import type { DropdownProps } from "./Dropdown";
import { DropdownListBoxAllOption } from "./DropdownListBoxAllOption";
import { DropdownListBoxSelectionBar } from "./DropdownListBoxSelectionBar";
import { DropdownListItem } from "./DropdownListItem";

type DropdownListBoxProps<TKey extends Key = Key> = DropdownProps<TKey> & {
  header?: React.ReactNode;
};

export const DropdownListBox = <TKey extends Key = Key>({
  selectionMode,
  value,
  onChange,
  items,
  showSelectionBar,
  showAllOption,
  header,
}: DropdownListBoxProps<TKey>) => {
  const isMultiple = selectionMode === "multiple";

  const getSelectedKeys = () => {
    if (isMultiple) {
      return value;
    }

    return typeof value === "undefined" ? [] : [value];
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto rounded-xs border border-secondary-300 bg-secondary-500 outline-hidden">
        {header}

        {isMultiple && (
          <>
            {showSelectionBar && (
              <DropdownListBoxSelectionBar
                onChange={onChange}
                items={items}
                className="t:flex hidden"
              />
            )}

            {showAllOption && (
              <DropdownListBoxAllOption
                value={value}
                onChange={onChange}
                items={items}
              />
            )}
          </>
        )}

        <AriaListBox
          className="max-h-[300px]"
          selectionMode={selectionMode}
          items={items}
          selectedKeys={getSelectedKeys()}
          shouldSelectOnPressUp
          onSelectionChange={(id) => {
            if (id === "all" && isMultiple) {
              onChange(items.map((item) => item.id));
              return;
            }

            const selectedIds = Array.from(id);
            if (!isMultiple && selectedIds.length === 0) {
              if (value) {
                onChange(value);
              }
              return;
            }

            if (isMultiple) {
              onChange(selectedIds as TKey[]);
            } else {
              onChange(selectedIds[0] as TKey);
            }
          }}
        >
          {items.map((item) => (
            <DropdownListItem
              key={item.id}
              {...item}
            />
          ))}
        </AriaListBox>
      </div>

      {isMultiple && (
        <DropdownListBoxSelectionBar
          onChange={onChange}
          items={items}
          className="t:hidden bg-secondary-400"
        />
      )}
    </>
  );
};
